// pages/News.js
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

// Метатеги
const MetaTags = () => (
  <>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <meta name="description" content="Новости компании Сонет-Дом. Последние объявления, акции и важная информация." />
    <meta name="keywords" content="Сонет-Дом, новости, объявления, акции, информация" />
  </>
);

// Глобальные стили для предотвращения горизонтального скроллинга
const GlobalStyles = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

// Стиль для секции
const Section = styled(motion.section)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f4f4f4;
  color: #333;
  transition: background 0.5s ease;
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Заголовок
const Title = styled.h1`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: #007aff;
  animation: fadeIn 1s ease-in-out;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1.5rem;
  }
`;

// Контейнер для новостей
const NewsContainer = styled(motion.div)`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 90%;
  max-width: 600px;
  margin: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  @media (max-width: 768px) {
    width: 100%;
    padding: 1.5rem;
  }
`;

// Подзаголовок
const Subtitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: #333;
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.5rem;
  }
`;

// Описание новости
const NewsDescription = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
  line-height: 1.5;
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
`;

// Дата новости
const NewsDate = styled.small`
  font-size: 0.9rem;
  color: #999;
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

// Фильтр
const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  max-width: 600px;
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

// Сортировка
const SortControls = styled.div`
  display: flex;
  align-items: center;
`;

// Кнопка сортировки
const SortButton = styled.button`
  padding: 8px 16px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, transform 0.2s ease-in-out;
  &:hover {
    background-color: #f0f0f0;
    transform: scale(1.05);
  }
`;

// Кнопка настроек
const SettingsButton = styled.button`
  padding: 8px 16px;
  font-size: 1.2em;
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: color 0.3s ease-in-out, transform 0.2s ease-in-out;
  &:hover {
    color: #555;
    transform: scale(1.05);
  }
`;

// Опции фильтрации
const AdvancedFilterOptions = styled.div`
  margin-top: 10px;
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  animation: slideDown 0.3s ease-in-out;
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Метка для инпута даты
const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-size: 1em;
  color: #555;
`;

// Инпут даты
const DateInput = styled.input`
  padding: 8px;
  font-size: 1em;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
`;

const News = () => {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [filterDate, setFilterDate] = useState('');
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' для новых к старым, 'asc' для старых к новым

  useEffect(() => {
    // Загрузка новостей из JSON-файла
    fetch('/data/news.json')
      .then(response => response.json())
      .then(data => {
        setNews(data);
        // Сортируем новости сразу после загрузки
        const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
        setFilteredNews(sortedData);
      })
      .catch(error => console.error('Ошибка загрузки новостей:', error));
  }, []);

  useEffect(() => {
    // Фильтрация и сортировка новостей
    let filtered = news;
    if (filterDate) {
      filtered = news.filter(item => item.date === filterDate);
    }
    if (sortOrder === 'desc') {
      filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else {
      filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    setFilteredNews(filtered);
  }, [filterDate, sortOrder, news]);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === 'desc' ? 'asc' : 'desc'));
  };

  const toggleAdvancedFilter = () => {
    setIsAdvancedFilterOpen(!isAdvancedFilterOpen);
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
  };

  const newsVariants = {
    hidden: { opacity: 0, y: 50, scale: 1 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
    hover: { scale: 1.02, transition: { duration: 0.3, ease: "easeInOut" } },
  };

  // Эффект для прокрутки страницы вверх при загрузке
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <MetaTags />
      <GlobalStyles>
        <Section
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          bgColor="#f4f4f4"
          textColor="#333"
          style={{ marginTop: '60px' }} // Добавляем отступ сверху
        >
          <Title>Новости</Title>

          <FilterContainer>
            <SortControls>
              <SortButton onClick={toggleSortOrder}>
                ↕
              </SortButton>
            </SortControls>
            <div className="advanced-filter">
              <SettingsButton onClick={toggleAdvancedFilter}>
                ⚙️
              </SettingsButton>
              {isAdvancedFilterOpen && (
                <AdvancedFilterOptions>
                  <Label htmlFor="date-filter">Фильтр по дате:</Label>
                  <DateInput
                    type="date"
                    id="date-filter"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                  />
                </AdvancedFilterOptions>
              )}
            </div>
          </FilterContainer>

          {filteredNews.map((item, index) => (
            <NewsContainer
              key={index}
              variants={newsVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true }}
            >
              <Subtitle>{item.title}</Subtitle>
              <NewsDescription dangerouslySetInnerHTML={{ __html: item.description.replace(/\n/g, '<br>') }} />
              <NewsDate>{item.date}</NewsDate>
            </NewsContainer>
          ))}
        </Section>
      </GlobalStyles>
    </>
  );
};

export default News;